import React, { useState } from 'react';
import {
  CardActions,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  PlayArrow as ExecuteIcon,
  History as HistoryIcon,
  Analytics as AnalyticsIcon,
  Delete as DeleteIcon,
  Timeline as TimelineIcon,
  Science as ExperimentIcon,
  RateReview as FeedbackIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { SPACING } from '../../../../theme';
import { createIOSButtonStyles } from '../../../../theme/iosButtonStyles';
import { adjustOpacity } from '../../../../theme/utils';
import PropTypes from 'prop-types';

/**
 * CompactCardActions Component
 * Displays primary action buttons and secondary actions menu
 */
const CompactCardActions = ({
  onEdit,
  onExecute,
  onOpenVersions,
  onOpenExecutions,
  onDelete,
  onCreateExperiment,
  onViewAnalytics,
  onOpenFeedback,
}) => {
  const theme = useTheme();
  const iosButtonStyles = createIOSButtonStyles(theme);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleMenuOpen = event => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <CardActions
      sx={{
        p: SPACING.md,
        pt: SPACING.sm,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {/* Primary Actions */}
      <Box display="flex" gap={1} flex={1}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={onEdit}
          sx={{
            ...iosButtonStyles.secondary,
            ...iosButtonStyles.small,
            textTransform: 'none',
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
            flex: 1,
          }}
        >
          Edit
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<ExecuteIcon />}
          onClick={onExecute}
          sx={{
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: '6px',
            padding: '4px 12px',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            border: 'none',
            boxShadow: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            minHeight: '28px',
            textTransform: 'none',
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
            flex: 1,
            '&:hover': {
              background: theme.palette.primary.dark,
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'scale(0.97)',
              background: theme.palette.primary.dark,
            },
          }}
        >
          Execute
        </Button>
      </Box>

      {/* Secondary Actions Menu */}
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{
          ml: 1,
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: adjustOpacity(theme.palette.text.secondary, 0.1),
            color: theme.palette.text.primary,
          },
        }}
      >
        <MoreIcon />
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            onOpenVersions();
            handleMenuClose();
          }}
        >
          <HistoryIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
          Version History
        </MenuItem>
        <MenuItem
          onClick={() => {
            onOpenExecutions();
            handleMenuClose();
          }}
        >
          <TimelineIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
          Execution History
        </MenuItem>
        <MenuItem
          onClick={() => {
            onCreateExperiment();
            handleMenuClose();
          }}
        >
          <ExperimentIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
          Create A/B Test
        </MenuItem>
        <MenuItem
          onClick={() => {
            onViewAnalytics();
            handleMenuClose();
          }}
        >
          <AnalyticsIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
          View Analytics
        </MenuItem>
        <MenuItem
          onClick={() => {
            onOpenFeedback();
            handleMenuClose();
          }}
        >
          <FeedbackIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
          Feedback
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            onDelete();
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
          Delete
        </MenuItem>
      </Menu>
    </CardActions>
  );
};

CompactCardActions.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onExecute: PropTypes.func.isRequired,
  onOpenVersions: PropTypes.func,
  onOpenExecutions: PropTypes.func,
  onDelete: PropTypes.func,
  onCreateExperiment: PropTypes.func,
  onViewAnalytics: PropTypes.func,
  onOpenFeedback: PropTypes.func,
};

export default CompactCardActions;
